import * as React from "react";
import { OfficeOpenIcon } from "./OfficeOpenIcons";
import { OfficeOpenSurface } from "./OfficeOpenSurface";
import {
  officeEditingService,
  type OfficeProviderStatus,
} from "../../services/officeEditingService";
import {
  speEditingService,
  type OfficeTarget,
  type WorkspaceStatus,
} from "../../services/speEditingService";
import { officeLoginReplacesApp, startOfficeMicrosoftLogin } from "../../utils/officeMicrosoftLogin";
import { DocumentWriteError } from "../../services/documentWriteError";
import { documentService } from "../../services/documentService";
import { editorTabVorbereiten } from "../neuAnlage/officeDokument";
/** Diese Fehler ändern sich durch Wiederholen nicht — dann hilft nur die Datei selbst. */
const DOWNLOAD_CODES = new Set(["spe_format_unsupported", "spe_ooxml_invalid", "spe_makros_unsupported"]);

/**
 * Altformate (.doc/.xls/.ppt, T-468): Excel, Word und PowerPoint am Desktop
 * bearbeiten sie im eigenen Format; Office im Browser zeigt sie nur an.
 */
export const istAltformat = (name?: string | null): boolean => /\.(doc|xls|ppt)$/i.test(name ?? "");
export const OfficeOpenDialog: React.FC<{
  documentId?: number;
  createDocument?: () => Promise<number>;
  onDismiss(): void;
  onOpened(status: WorkspaceStatus | null, documentId: number): void;
  activeWorkspace?: boolean;
  /** OnlyOffice bleibt während der Übergangszeit auf seiner bestehenden Vollbildroute. */
  openOnlyOffice?: (id: number, tab: Window | null) => void;
  prepareTab?: () => Window | null;
  discardTab?: (tab: Window | null) => void;
  presentation?: "dialog" | "popover";
  anchor?: HTMLElement;
  fileName?: string;
  onDetails?: () => void;
  /** Rückkehr aus OAuth: höchstens ein Start, ohne neue Popup-Anforderung. */
  resumeTarget?: OfficeTarget;
  /** Die Anmeldung lief im eigens geöffneten Tab; nur dann darf Office diesen Tab übernehmen. */
  resumeInOwnTab?: boolean;
}> = ({
  documentId,
  createDocument,
  onDismiss,
  onOpened,
  activeWorkspace,
  openOnlyOffice,
  prepareTab,
  discardTab,
  presentation = "dialog",
  anchor,
  fileName,
  onDetails,
  resumeTarget,
  resumeInOwnTab,
}) => {
  const [provider, setProvider] = React.useState<OfficeProviderStatus | null>(
    null,
  );
  const [busy, setBusy] = React.useState(false);
  const running = React.useRef(false);
  const id = React.useRef(documentId);
  /** Nur ein in DIESEM Dialog frisch angelegtes Dokument darf beim Scheitern wieder weg. */
  const createdHere = React.useRef(false);
  const [error, setError] = React.useState<string | null>(null);
  const [errorCode, setErrorCode] = React.useState<string | null>(null);
  const [manualUrl, setManualUrl] = React.useState<{ url: string; blocked: boolean } | null>(null);
  /** Gestartet, aber ohne eigenen Tab: Das Popup bleibt mit dem Link stehen. */
  const [started, setStarted] = React.useState<OfficeTarget | null>(null);
  /** Die Anmeldung würde KMVS neu laden — erst fragen. */
  const [loginAsk, setLoginAsk] = React.useState<OfficeTarget | null>(null);
  const resumed = React.useRef(false);
  React.useEffect(() => {
    let alive = true;
    void officeEditingService
      .status({ fresh: true })
      .then((p) => alive && setProvider(p));
    return () => {
      alive = false;
    };
  }, []);
  const start = async (target: OfficeTarget, afterLogin = false) => {
    if (running.current) return;
    running.current = true;
    setBusy(true);
    setError(null);
    setErrorCode(null);
    setManualUrl(null);
    // Im bewussten Klick reservieren, vor jeglichem await. Die neue Seite bekommt keinen Opener.
    let tab: Window | null = null;
    try {
      if (target === "web" && !afterLogin)
        tab = prepareTab ? prepareTab() : editorTabVorbereiten("Dokument wird geöffnet …");
    } catch {
      /* Manuellen Link anbieten */
    }
    if (tab) tab.opener = null;
    const tabReserved = !!tab;
    try {
      const current = await officeEditingService.status({ fresh: true });
      setProvider(current);
      if (activeWorkspace && current.provider !== "spe")
        throw new Error(
          "Die bestehende Arbeitskopie muss zunächst gesichert und abgeschlossen werden. Neue Bearbeitung ist derzeit gesperrt.",
        );
      if (!current.enabled || current.provider === "disabled")
        throw new Error(
          "Neue Office-Bearbeitungen sind derzeit nicht verfügbar. Bestehende Arbeitskopien können weiter gesichert werden.",
        );
      if (current.provider === "onlyoffice" && target === "desktop")
        throw new Error(
          "Die Desktop-Bearbeitung ist derzeit nicht eingerichtet. Bitte „Im Web“ wählen.",
        );
      if (id.current == null) {
        if (!createDocument)
          throw new Error("Das Dokument ist nicht verfügbar.");
        id.current = await createDocument();
        createdHere.current = true;
      }
      if (current.provider === "onlyoffice") {
        if (openOnlyOffice && tab && !tab.closed) {
          try {
            tab.sessionStorage.setItem(
              "kmvs-office-web-choice",
              String(id.current),
            );
          } catch {
            /* Nur eine Bedienhilfe, keine Berechtigung */
          }
          openOnlyOffice(id.current, tab);
          onOpened(null, id.current);
          onDismiss();
          return;
        }
        const url = `/dokumente/${id.current}/bearbeiten?office=web`;
        if (tab && !tab.closed) {
          tab.location.replace(url);
          onDismiss();
        } else setManualUrl({ url, blocked: !tabReserved });
        onOpened(null, id.current);
        return;
      }
      const opened = await speEditingService.open(id.current, target);
      onOpened(opened, id.current);
      if (target === "web") {
        if (afterLogin && resumeInOwnTab) {
          // Dieser Tab wurde nur für die Anmeldung geöffnet; Office übernimmt ihn.
          window.location.replace(opened.launchUrl);
          return;
        }
        // Der KMVS-Tab selbst wird nie durch Office ersetzt.
        if (!afterLogin && tab && !tab.closed) tab.location.replace(opened.launchUrl);
        else {
          // Ohne eigenen Tab braucht der Nutzer den Link — nur dann bleibt das Popup.
          setManualUrl({ url: opened.launchUrl, blocked: afterLogin || !tabReserved });
          setStarted(target);
          return;
        }
      } else {
        // Der Browser kann nur das Protokoll anfordern, keinen Appstart bestätigen.
        window.open(opened.launchUrl, "_self");
      }
      // Gestartet: kein Hinweis-Popup mehr, das man wegklicken müsste (User 05.10.2026).
      onDismiss();
    } catch (e) {
      if (e instanceof DocumentWriteError && e.requiresMicrosoftLogin && id.current != null && !afterLogin) {
        if (officeLoginReplacesApp(target, tab)) {
          if (discardTab) discardTab(tab);
          else tab?.close();
          setLoginAsk(target);
          return;
        }
        try {
          await startOfficeMicrosoftLogin(id.current, target, tab);
          onDismiss();
          return;
        } catch (authError) {
          e = authError;
        }
      }
      if (discardTab) discardTab(tab);
      else tab?.close();
      // T-208: Scheitert der Start eines eben angelegten Dokuments, bleibt sonst
      // ein leeres Dokument im Vorgang liegen — wieder in den Papierkorb legen.
      // Ein erneuter Versuch legt dann ein neues an. Bestehende nie anfassen.
      if (createdHere.current && id.current != null) {
        const leer = id.current;
        createdHere.current = false;
        id.current = documentId;
        await documentService.remove(leer).catch(() => undefined);
      }
      setError(
        e instanceof Error ? e.message : "Office konnte nicht geöffnet werden.",
      );
      setErrorCode(e instanceof DocumentWriteError ? e.code : null);
    } finally {
      running.current = false;
      setBusy(false);
    }
  };
  const login = async (target: OfficeTarget) => {
    if (running.current || id.current == null) return;
    running.current = true;
    setBusy(true);
    setError(null);
    try {
      await startOfficeMicrosoftLogin(id.current, target, null);
    } catch (e) {
      setLoginAsk(null);
      setError(e instanceof Error ? e.message : "Microsoft-Anmeldung konnte nicht gestartet werden.");
      running.current = false;
      setBusy(false);
    }
    // Im Erfolgsfall lädt die Seite neu; das Popup bleibt bis dahin gesperrt.
  };
  const download = async () => {
    if (id.current == null) return;
    try {
      const blob = await documentService.fetchBlob(id.current);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || "Dokument";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      setError("Das Dokument konnte nicht heruntergeladen werden.");
    }
  };
  React.useEffect(() => {
    if (!resumeTarget || !provider || resumed.current) return;
    resumed.current = true;
    void start(resumeTarget, true);
    // Die Rückkehr ist einmalig; Änderungen am Status starten kein zweites Mal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, resumeTarget]);
  const disabled =
    busy ||
    !provider ||
    !provider.enabled ||
    (activeWorkspace === true && provider.provider !== "spe");
  const feedback = <>
    {!provider && <p role="status">Office-Verfügbarkeit wird geprüft…</p>}
    {busy && <p role="status">Dokument wird geöffnet…</p>}
    {provider && !provider.enabled && <p>Office-Bearbeitung ist derzeit nicht verfügbar.</p>}
    {activeWorkspace && provider && provider.provider !== "spe" && <p>Die bestehende Arbeitskopie muss zunächst gesichert und abgeschlossen werden.</p>}
    {error && <p role="alert">{error}</p>}
    {error && errorCode && DOWNLOAD_CODES.has(errorCode) && id.current != null &&
      <p><a href="#" onClick={event => { event.preventDefault(); void download(); }}>Herunterladen</a></p>}
    {manualUrl && <p>{manualUrl.blocked ? "Der Browser hat das neue Fenster blockiert." : "Das Fenster wurde geschlossen."} <a href={manualUrl.url} target="_blank" rel="noopener noreferrer">Im Web öffnen</a></p>}
  </>;
  const title = createDocument ? "Neues Office-Dokument" : "Office-Dokument öffnen";
  if (loginAsk)
    return (
      <OfficeOpenSurface presentation={presentation} anchor={anchor} title="Microsoft-Anmeldung nötig"
        busy={busy} onDismiss={onDismiss}>
        <p className="office-open-question">
          Für Office musst du dich bei Microsoft anmelden. KMVS wird dabei neu geladen –
          ungespeicherte Eingaben gehen verloren.
        </p>
        <div className="office-open-foot">
          <button type="button" className="office-open-cancel" disabled={busy} onClick={() => setLoginAsk(null)}>Abbrechen</button>
          <button type="button" className="office-open-cancel office-open-primary" disabled={busy} onClick={() => void login(loginAsk)}>Jetzt anmelden</button>
        </div>
        <div className="office-open-feedback">{busy && <p role="status">Anmeldung wird gestartet…</p>}</div>
      </OfficeOpenSurface>
    );
  if (started)
    return (
      <OfficeOpenSurface presentation={presentation} anchor={anchor} title={title} onDismiss={onDismiss}>
        {fileName && <p className="office-open-file" title={fileName}>{fileName}</p>}
        <div className="office-open-feedback">
          <p role="status">Die Arbeitskopie ist bereit.</p>
          {manualUrl && <p>{manualUrl.blocked ? "Der Browser hat das neue Fenster blockiert." : "Das Fenster wurde geschlossen."} <a href={manualUrl.url} target="_blank" rel="noopener noreferrer">Im Web öffnen</a></p>}
        </div>
        <div className="office-open-foot">
          <button type="button" className="office-open-cancel" onClick={onDismiss}>Schließen</button>
        </div>
      </OfficeOpenSurface>
    );
  return (
    <OfficeOpenSurface presentation={presentation} anchor={anchor}
      title={title}
      busy={busy} onDismiss={onDismiss}>
      {fileName && <p className="office-open-file" title={fileName}>{fileName}</p>}
      {presentation === "dialog" && <p className="office-open-question">Wo möchtest du das Dokument bearbeiten?</p>}
      {istAltformat(fileName) && (
        <p className="office-open-hinweis" role="note">
          Altes Format: Bearbeiten geht am <b>Desktop</b>. Im Web lässt sich die Datei nur ansehen.
        </p>
      )}
      <div className="office-open-choices">
        <button type="button" className="office-open-choice" disabled={disabled} onClick={() => void start("web")}>
          <OfficeOpenIcon kind="web" size={24} />Im Web
        </button>
        <button type="button" className="office-open-choice" disabled={disabled || provider?.provider !== "spe"} onClick={() => void start("desktop")}>
          <OfficeOpenIcon kind="desktop" size={24} />Desktop
        </button>
      </div>
      <div className="office-open-feedback">{feedback}</div>
      {onDetails && <button type="button" className="office-open-detail" disabled={busy} onClick={onDetails}>
        <OfficeOpenIcon kind="history" size={18} />Fassungen und Sicherung
      </button>}
      {presentation === "dialog" && <div className="office-open-foot">
        <button type="button" className="office-open-cancel" disabled={busy} onClick={onDismiss}>Abbrechen</button>
      </div>}
    </OfficeOpenSurface>
  );
};
